import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where, DocumentData, updateDoc, doc, getDoc, addDoc, onSnapshot } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { format } from "date-fns";
import { auth } from "../../lib/firebase";

interface Enrollment {
  id: string;
  studentId: string;
  courseId: string;
  status: string;
  branch?: string;
  courseFee?: number;
  enrolledAt?: { seconds: number };
  createdAt?: { seconds: number };
  paymentReceiptUrl?: string;
  rejectionReason?: string;
  completedHours?: number;
  totalHours?: number;
}


interface Student {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface Course {
  id: string;
  name?: string;
  title?: string;
}

const statusOptions = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, i) => currentYear - i);
};

const StaffEnrollments: React.FC = () => {
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [status, setStatus] = useState("pending"); // Changed default to show pending
  const [month, setMonth] = useState("all");
  const [year, setYear] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [branch, setBranch] = useState<string | null>(null);
  const [selectedEnrollment, setSelectedEnrollment] = useState<Enrollment | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  // Approve payment handler
  const handleApprovePayment = async (enrollmentId: string) => {
    setLoading(true);
    try {
      const enrollmentRef = doc(db, "enrollments", enrollmentId);
      const enrollmentDoc = await getDoc(enrollmentRef);
      const enrollmentData = enrollmentDoc.data();
      
      await updateDoc(enrollmentRef, { 
        status: "approved",
        approvedAt: new Date()
      });

      // Create notification for student
      await addDoc(collection(db, "notifications"), {
        userId: enrollmentData?.studentId,
        type: "payment_approved",
        title: "Payment Approved",
        message: "Your payment has been verified and approved. You can now request a schedule.",
        read: false,
        createdAt: new Date()
      });
      
      // Real-time listeners will automatically update the UI
    } catch (err) {
      setError("Failed to approve payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Reject payment handler
  const handleRejectPayment = async () => {
    if (!selectedEnrollment || !rejectionReason.trim()) {
      setError("Please provide a reason for rejection.");
      return;
    }
    
    setLoading(true);
    try {
      const enrollmentRef = doc(db, "enrollments", selectedEnrollment.id);
      await updateDoc(enrollmentRef, { 
        status: "rejected",
        rejectionReason: rejectionReason.trim(),
        rejectedAt: new Date()
      });

      // Create notification for student
      await addDoc(collection(db, "notifications"), {
        userId: selectedEnrollment.studentId,
        type: "payment_rejected",
        title: "Payment Rejected",
        message: `Your payment has been rejected. Reason: ${rejectionReason.trim()}`,
        read: false,
        createdAt: new Date()
      });

      // Real-time listeners will automatically update the UI
      setShowRejectDialog(false);
      setSelectedEnrollment(null);
      setRejectionReason("");
    } catch (err) {
      setError("Failed to reject payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!auth.currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    const unsubscribers: (() => void)[] = [];

    const setupRealtimeListeners = async () => {
      try {
        // Get current staff user's branch
        const userDoc = await getDoc(doc(db, "users", auth.currentUser!.uid));
        const userData = userDoc.data();
        if (userData && userData.branch) {
          const branchDoc = await getDoc(doc(db, "branches", userData.branch));
          const branchData = branchDoc.data();
          setBranch(branchData?.name || userData.branch);
        }

        // Set up real-time listener for enrollments
        const enrollmentsUnsubscribe = onSnapshot(collection(db, "enrollments"), (snapshot) => {
          const enrollmentsData = snapshot.docs.map((doc) => ({ 
            id: doc.id, 
            ...doc.data() 
          })) as Enrollment[];
          setEnrollments(enrollmentsData);

          // Get unique studentIds and courseIds from enrollments
          const studentIds = Array.from(new Set(enrollmentsData.map(e => e.studentId)));
          const courseIds = Array.from(new Set(enrollmentsData.map(e => e.courseId)));

          // Fetch students for the enrollments
          if (studentIds.length > 0) {
            fetchStudents(studentIds);
          }

          // Fetch courses for the enrollments
          if (courseIds.length > 0) {
            fetchCourses(courseIds);
          }
        });
        unsubscribers.push(enrollmentsUnsubscribe);

        setLoading(false);
      } catch (error) {
        console.error("Error setting up real-time listeners:", error);
        setError("Failed to load data. Please try again.");
        setLoading(false);
      }
    };

    const fetchStudents = async (studentIds: string[]) => {
      try {
        const studentsData: Student[] = [];
        for (const id of studentIds) {
          const userDoc = await getDoc(doc(db, "users", id));
          if (userDoc.exists()) {
            const data = userDoc.data();
            studentsData.push({
              id: userDoc.id,
              firstName: data.firstName,
              lastName: data.lastName,
              email: data.email,
            });
          }
        }
        setStudents(studentsData);
      } catch (error) {
        console.error("Error fetching students:", error);
      }
    };

    const fetchCourses = async (courseIds: string[]) => {
      try {
        const coursesData: Course[] = [];
        for (const id of courseIds) {
          const courseDoc = await getDoc(doc(db, "courses", id));
          if (courseDoc.exists()) {
            coursesData.push({ id: courseDoc.id, ...courseDoc.data() } as Course);
          }
        }
        setCourses(coursesData);
      } catch (error) {
        console.error("Error fetching courses:", error);
      }
    };

    setupRealtimeListeners();

    // Cleanup function to unsubscribe from all listeners
    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, []);

  const filteredEnrollments = useMemo(() => {
    return enrollments.filter((enrollment) => {
      const student = students.find((s) => s.id === enrollment.studentId);
      const course = courses.find((c) => c.id === enrollment.courseId);
      // Use enrolledAt if present, otherwise fallback to createdAt
      const dateField = enrollment.enrolledAt || enrollment.createdAt;
      if (!dateField || typeof dateField.seconds !== "number") {
        return false;
      }
      const createdAt = new Date(dateField.seconds * 1000);
      const matchesStatus = status === "all" || enrollment.status === status;
      const matchesMonth = month === "all" || createdAt.getMonth() + 1 === Number(month);
      const matchesYear = year === "all" || createdAt.getFullYear() === Number(year);
      const studentName = student ? `${student.firstName || ""} ${student.lastName || ""}`.trim() : "";
      const matchesSearch =
        !search ||
        (student &&
          ((studentName && studentName.toLowerCase().includes(search.toLowerCase())) ||
            (student.email && student.email.toLowerCase().includes(search.toLowerCase())))) ||
        (course && ((course.name && course.name.toLowerCase().includes(search.toLowerCase())) || (course.title && course.title.toLowerCase().includes(search.toLowerCase()))));
      return matchesStatus && matchesMonth && matchesYear && matchesSearch;
    });
  }, [enrollments, students, courses, status, month, year, search]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Enrollments</h1>
        {branch && (
          <div className="text-sm text-muted-foreground">
            Branch: {branch}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-4 mb-6 items-end">
        <div className="flex flex-col flex-1 min-w-64">
          <label className="mb-2 text-sm font-medium">Search</label>
          <Input
            placeholder="Search by student, email, or course"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex flex-col">
          <label className="mb-2 text-sm font-medium">Status</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col">
          <label className="mb-2 text-sm font-medium">Month</label>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {Array.from({ length: 12 }, (_, i) => (
                <SelectItem key={i + 1} value={(i + 1).toString()}>
                  {format(new Date(2020, i, 1), "MMMM")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col">
          <label className="mb-2 text-sm font-medium">Year</label>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-24">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {getYearOptions().map((y) => (
                <SelectItem key={y} value={y.toString()}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-blue-600 border-t-transparent"></div>
            <p className="mt-2">Loading enrollment data...</p>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border">
            <thead>
              <tr>
                <th className="px-4 py-2 border">Student Name</th>
                <th className="px-4 py-2 border">Course Name</th>
                <th className="px-4 py-2 border">Status</th>
                <th className="px-4 py-2 border">Progress</th>
                <th className="px-4 py-2 border">Payment Proof</th>
                <th className="px-4 py-2 border">Date</th>
                <th className="px-4 py-2 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEnrollments.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-4">
                    No enrollments found.
                  </td>
                </tr>
              ) : (
                filteredEnrollments.map((enrollment) => {
                  const student = students.find((s) => s.id === enrollment.studentId);
                  const course = courses.find((c) => c.id === enrollment.courseId);
                  const studentName = student ? `${student.firstName || ""} ${student.lastName || ""}`.trim() : "";
                  return (
                    <tr key={enrollment.id}>
                      <td className="px-4 py-2 border">{studentName || enrollment.studentId || "Unknown Student"}</td>
                      <td className="px-4 py-2 border">{course?.name || course?.title || enrollment.courseId || "Unknown Course"}</td>
                      <td className="px-4 py-2 border">
                        <span
                          className={
                            "inline-block px-2 py-1 rounded text-xs " +
                            (enrollment.status === "approved"
                              ? "bg-green-100 text-green-700"
                              : enrollment.status === "completed"
                              ? "bg-green-100 text-green-800"
                              : enrollment.status === "pending"
                              ? "bg-orange-100 text-orange-700"
                              : enrollment.status === "rejected"
                              ? "bg-red-100 text-red-700"
                              : "bg-gray-100 text-gray-700")
                          }
                        >
                          {enrollment.status === "pending" ? "Pending" : 
                           enrollment.status.charAt(0).toUpperCase() + enrollment.status.slice(1)}
                        </span>
                        {enrollment.status === "rejected" && enrollment.rejectionReason && (
                          <div className="mt-1 text-xs text-red-600">
                            Reason: {enrollment.rejectionReason}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 border">
                        {enrollment.status === "completed" ? (
                          <span className="inline-block px-2 py-1 rounded text-xs bg-green-100 text-green-800">
                            Completed
                          </span>
                        ) : enrollment.status === "approved" && enrollment.completedHours !== undefined && enrollment.totalHours !== undefined ? (
                          <div className="flex flex-col space-y-1">
                            <div className="text-xs text-gray-600">
                              {enrollment.completedHours}/{enrollment.totalHours} hours
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${Math.min((enrollment.completedHours / enrollment.totalHours) * 100, 100)}%` }}
                              ></div>
                            </div>
                            <div className="text-xs text-gray-500">
                              {Math.round(Math.min((enrollment.completedHours / enrollment.totalHours) * 100, 100))}%
                            </div>
                          </div>
                        ) : enrollment.status === "approved" ? (
                          <span className="text-xs text-gray-500">No progress data</span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-2 border">
                        <div className="space-y-2">
                          <div className="flex space-x-2">
                            {enrollment.paymentReceiptUrl ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(enrollment.paymentReceiptUrl, '_blank')}
                                className="flex-1"
                              >
                                View Receipt
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled
                                className="flex-1"
                              >
                                No Receipt
                              </Button>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2 border">
                        {format(new Date((enrollment.enrolledAt || enrollment.createdAt)?.seconds * 1000), "yyyy-MM-dd")}
                      </td>
                      <td className="px-4 py-2 border">
                        {enrollment.status === "pending" && (
                          <div className="space-y-2">
                            <div className="flex space-x-2">
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white flex-1"
                                disabled={loading}
                                onClick={() => handleApprovePayment(enrollment.id)}
                              >
                                {loading ? "Approving..." : "Approve"}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                disabled={loading}
                                className="flex-1"
                                onClick={() => {
                                  setSelectedEnrollment(enrollment);
                                  setShowRejectDialog(true);
                                }}
                              >
                                {loading ? "Processing..." : "Reject"}
                              </Button>
                            </div>
                          </div>
                        )}
                        {enrollment.status === "approved" && (
                          <div className="text-center">
                            <div className="text-xs text-green-600">✓ Approved</div>
                            <div className="text-xs text-gray-500 mt-1">Payment Verified</div>
                          </div>
                        )}
                        {enrollment.status === "rejected" && (
                          <div className="text-center">
                            <div className="text-xs text-red-600">✗ Rejected</div>
                            <div className="text-xs text-gray-500 mt-1">Payment Declined</div>
                          </div>
                        )}
                        {enrollment.status !== "pending" && enrollment.status !== "approved" && enrollment.status !== "rejected" && (
                          <div className="text-center">
                            {enrollment.status === "completed" ? (
                              <span className="inline-block px-2 py-1 rounded text-xs bg-green-100 text-green-800">
                                Completed
                              </span>
                            ) : (
                              <div className="text-xs text-gray-600 capitalize">{enrollment.status}</div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Rejection Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>Please provide a reason for rejecting this payment:</p>
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows={3}
            />
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectDialog(false);
                  setSelectedEnrollment(null);
                  setRejectionReason("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={loading || !rejectionReason.trim()}
                onClick={handleRejectPayment}
              >
                {loading ? "Rejecting..." : "Reject Payment"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffEnrollments;
